import React, { useState, useEffect } from 'react';
import { staffAPI } from '../../config/api';
import APP_COLOURS from '../../config/colors';

const StaffSalesView = ({ staffMember, onClose }) => {
  const [salesData, setSalesData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('sales');
  const [dateRange, setDateRange] = useState('30'); // Default 30 days

  useEffect(() => {
    if (staffMember) {
      fetchStaffData();
    }
  }, [staffMember, dateRange]);

  const fetchStaffData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = dateRange !== 'custom' ? { days: parseInt(dateRange) } : null;
      
      // Fetch both sales and attendance data
      const [salesResponse, attendanceResponse] = await Promise.all([
        staffAPI.getStaffSales(staffMember.id, params),
        staffAPI.getStaffAttendance(staffMember.id, params)
      ]);
      
      setSalesData(salesResponse);
      setAttendanceData(attendanceResponse);
    } catch (err) {
      console.error('Error fetching staff data:', err);
      setError(err.message || 'Failed to fetch staff data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `R ${parseFloat(amount || 0).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!staffMember) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: APP_COLOURS.BORDER }}>
          <div>
            <h2 className="text-2xl font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              {staffMember.first_name} {staffMember.last_name}
            </h2>
            <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR_LIGHT }}>
              {staffMember.email} • {staffMember.brands?.join(', ') || 'No brands assigned'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Date Range Selector */}
        <div className="p-4 border-b" style={{ borderColor: APP_COLOURS.BORDER }}>
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              Date Range:
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-1 border rounded-md text-sm"
              style={{ borderColor: APP_COLOURS.BORDER }}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: APP_COLOURS.BORDER }}>
          <button
            onClick={() => setActiveTab('sales')}
            className={`px-6 py-3 font-medium text-sm ${
              activeTab === 'sales'
                ? 'border-b-2 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            style={{ borderColor: activeTab === 'sales' ? APP_COLOURS.BLUE : 'transparent' }}
          >
            Sales ({salesData?.count || 0})
          </button>
          <button
            onClick={() => setActiveTab('attendance')}
            className={`px-6 py-3 font-medium text-sm ${
              activeTab === 'attendance'
                ? 'border-b-2 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            style={{ borderColor: activeTab === 'attendance' ? APP_COLOURS.BLUE : 'transparent' }}
          >
            Attendance ({attendanceData?.count || 0})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: '60vh' }}>
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: APP_COLOURS.BLUE }}></div>
              <span className="ml-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>Loading...</span>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchStaffData}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {activeTab === 'sales' && (
                <div className="p-6">
                  {/* Sales Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-500">Total Sales</h3>
                      <p className="text-2xl font-bold" style={{ color: APP_COLOURS.BLUE }}>
                        {formatCurrency(salesData?.totalSales)}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-500">Transactions</h3>
                      <p className="text-2xl font-bold" style={{ color: APP_COLOURS.BLUE }}>
                        {salesData?.totalTransactions || 0}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-500">Average Sale</h3>
                      <p className="text-2xl font-bold" style={{ color: APP_COLOURS.BLUE }}>
                        {formatCurrency(
                          salesData?.totalTransactions > 0 
                            ? salesData.totalSales / salesData.totalTransactions 
                            : 0
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Sales List */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      Recent Sales
                    </h3>
                    {salesData?.sales?.length > 0 ? (
                      salesData.sales.map((sale) => (
                        <div key={sale.id} className="border rounded-lg p-4" style={{ borderColor: APP_COLOURS.BORDER }}>
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                                {formatCurrency(sale.trs)} • {sale.salesItems || 'No items listed'}
                              </p>
                              <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR_LIGHT }}>
                                {sale.retailer?.title || sale.retailer} • {sale.mall?.title || sale.mall}
                              </p>
                              <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR_LIGHT }}>
                                Customer: {sale.customer_gender} • {sale.customer_age_range} • {sale.customer_ethnicity}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR_LIGHT }}>
                                {formatDate(sale.dateCreated)}
                              </p>
                              <p className="text-sm font-medium" style={{ color: APP_COLOURS.BLUE }}>
                                IPT: {formatCurrency(sale.ipt)} • ARS: {formatCurrency(sale.ars)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center py-8" style={{ color: APP_COLOURS.TEXTCOLOR_LIGHT }}>
                        No sales data found for the selected period.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'attendance' && (
                <div className="p-6">
                  {/* Attendance Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-500">Days Present</h3>
                      <p className="text-2xl font-bold" style={{ color: APP_COLOURS.BLUE }}>
                        {attendanceData?.totalDaysPresent || 0}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-500">Total Hours</h3>
                      <p className="text-2xl font-bold" style={{ color: APP_COLOURS.BLUE }}>
                        {attendanceData?.totalHours?.toFixed(1) || '0.0'}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-500">Avg Hours/Day</h3>
                      <p className="text-2xl font-bold" style={{ color: APP_COLOURS.BLUE }}>
                        {attendanceData?.totalDaysPresent > 0 
                          ? (attendanceData.totalHours / attendanceData.totalDaysPresent).toFixed(1)
                          : '0.0'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Attendance List */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      Recent Attendance
                    </h3>
                    {attendanceData?.attendance?.length > 0 ? (
                      attendanceData.attendance.map((record) => (
                        <div key={record.id} className="border rounded-lg p-4" style={{ borderColor: APP_COLOURS.BORDER }}>
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                                {formatDate(record.date || record.dateCreated)}
                              </p>
                              <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR_LIGHT }}>
                                Status: {record.status || 'Unknown'}
                              </p>
                            </div>
                            <div className="text-right">
                              {record.clockIn && (
                                <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                                  In: {record.clockIn} • Out: {record.clockOut || 'Not clocked out'}
                                </p>
                              )}
                              {record.hoursWorked && (
                                <p className="text-sm font-medium" style={{ color: APP_COLOURS.BLUE }}>
                                  {record.hoursWorked} hours
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center py-8" style={{ color: APP_COLOURS.TEXTCOLOR_LIGHT }}>
                        No attendance data found for the selected period.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffSalesView;
